<script setup>
import {ref, computed, onMounted, defineAsyncComponent} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import User from '@classes/User';
/***/
import { useUserStore } from '@stores/user';
import { useApplicationStore } from '@stores/application';
import { useHistoryStore } from '@stores/history';
/***/
import { useAuthUser } from '@compositions/Auth';

// Composants
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const EditUserInternalForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/Users/Forms/EditUserInternalForm'));
const EditUserCustomerForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/Users/Forms/EditUserCustomerForm'));

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Store
const applicationStore = useApplicationStore();
const userStore = useUserStore();
const historyStore = useHistoryStore();

const currentApplication = computed(() => applicationStore.getCurrent);

const { authUser, authUserHasPermission } = useAuthUser({});
const userTypeParam = route.params.userType;
const allowed = ref(false);
const pageTitle = ref(null);
const pageDescription = ref(null);

const userTypeCamelCase = User.getCamelTypeBySlug(userTypeParam);

onMounted(() => {
    const requiredPermission = User.getCreatePermissionByType(userTypeCamelCase);
    const canConsult = (requiredPermission !== null && authUserHasPermission(requiredPermission) && userTypeCamelCase !== 'agencyManager');

    if(! canConsult) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {
        const titleKey = 'page.admin.users.:type.create.title'.replace(':type', userTypeCamelCase);
        const descriptionKey = 'page.admin.users.:type.create.description'.replace(':type', userTypeCamelCase);
        document.title = t(titleKey) + ' - ' + currentApplication.value.name;
        pageTitle.value = t(titleKey);
        pageDescription.value = t(descriptionKey);

        allowed.value = true;
    }
});

// Méthode exécutée après la création d'un utilisateur
const onCreateUser = ((user) => {
    userStore.updateUser(user);
    historyStore.setMustPreserveScrollPosition(true);
    router.push({
        name: 'admin.users.edit',
        params: {
            userType: userTypeParam,
            userId: user.id,
        },
        query: {
            'from-create': 1
        }
    });
});

// Retourne le nom du composant pour le formulaire d'édition
const getFormComponentName = (() => {
    return (userTypeCamelCase === 'customer') ? EditUserCustomerForm : EditUserInternalForm;
});
</script>

<template>
    <layout v-if="allowed" wrapperContentClasses="wrapper_edit-add">
        <div class="content_header">
            <div class="content-header_title">
                <h1 class="first-title">{{ pageTitle }}</h1>
                <p>{{ pageDescription }}</p>
            </div>
        </div>
        <component
            :user-type="userTypeCamelCase"
            v-on:user-save="onCreateUser($event)"
            :is="getFormComponentName()"
        ></component>
    </layout>
</template>
